import { useCallback, useRef } from 'react';
import * as Y from 'yjs';
import { useReactFlow } from '@xyflow/react';
import { useScreenToCanvasCoords } from '../hooks/use-screen-to-canvas-coords';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';
import styles from './eraser-overlay.module.css';

interface Props {
  doc: Y.Doc;
}

function eraseAt(x: number, y: number, doc: Y.Doc, getNodes: ReturnType<typeof useReactFlow>['getNodes'], binding: CanvasBinding = TOP_LEVEL_BINDING) {
  const hits = getNodes().filter(n => {
    if (n.type !== 'freedraw') return false;
    const nx = n.position.x;
    const ny = n.position.y;
    const nw = n.measured?.width ?? 0;
    const nh = n.measured?.height ?? 0;
    return x >= nx && x <= nx + nw && y >= ny && y <= ny + nh;
  });
  if (hits.length === 0) return;

  const { nodes: nodesMap, zOrder } = canvasRoots(doc, binding);
  doc.transact(() => {
    const arr = zOrder.toArray();
    const indices: number[] = [];
    for (const n of hits) {
      const idx = arr.indexOf(n.id);
      if (idx !== -1) indices.push(idx);
      nodesMap.delete(n.id);
    }
    indices.sort((a, b) => b - a);
    for (const idx of indices) zOrder.delete(idx, 1);
  }, doc.clientID);
}

export function EraserOverlay({ doc }: Props) {
  const { getNodes } = useReactFlow();
  const screenToCanvas = useScreenToCanvasCoords();
  const binding = useCanvasBinding();
  const activeRef = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    activeRef.current = true;
    const pos = screenToCanvas(e.clientX, e.clientY);
    eraseAt(pos.x, pos.y, doc, getNodes, binding);
  }, [doc, getNodes, screenToCanvas, binding]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!activeRef.current) return;
    const pos = screenToCanvas(e.clientX, e.clientY);
    eraseAt(pos.x, pos.y, doc, getNodes, binding);
  }, [doc, getNodes, screenToCanvas, binding]);

  const onPointerUp = useCallback(() => {
    activeRef.current = false;
  }, []);

  return (
    <div
      data-testid="eraser-overlay"
      className={styles.overlay}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    />
  );
}
